import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const ALERT_TYPES = {
  warning: {
    bg: 'bg-warning',
    text: 'text-warning-foreground',
    icon: 'AlertTriangle',
    borderColor: 'border-warning',
  },
  error: {
    bg: 'bg-error',
    text: 'text-error-foreground',
    icon: 'AlertCircle',
    borderColor: 'border-error',
  },
  success: {
    bg: 'bg-success',
    text: 'text-success-foreground',
    icon: 'CheckCircle',
    borderColor: 'border-success',
  },
  info: {
    bg: 'bg-primary',
    text: 'text-primary-foreground',
    icon: 'Info',
    borderColor: 'border-primary',
  },
};

// Sample initial alerts - in production these would come from a data source/context
const INITIAL_ALERTS = [
  {
    id: 1,
    type: 'warning',
    title: 'Low Inventory Alert',
    message: 'SKU-2847 (Premium Rice 25kg) is below reorder threshold in Visayas region.',
    timestamp: '2 min ago',
    dismissible: true,
    actionLabel: 'View Inventory',
    actionPath: '/sales-ordering-system',
  },
  {
    id: 2,
    type: 'info',
    title: 'AI Insight Available',
    message: 'New sales trend analysis for Q1 2026 is ready. NCR region shows 18% growth opportunity.',
    timestamp: '15 min ago',
    dismissible: true,
    actionLabel: 'View Report',
    actionPath: '/ai-intelligence-reports',
  },
];

export default function NotificationAlertBar({ alerts: externalAlerts, onAlertAction }) {
  const [alerts, setAlerts] = useState(externalAlerts || INITIAL_ALERTS);
  const [currentIndex, setCurrentIndex] = useState(0);

  const dismissAlert = (id) => {
    setAlerts((prev) => {
      const updated = prev?.filter((a) => a?.id !== id);
      if (currentIndex >= updated?.length && updated?.length > 0) {
        setCurrentIndex(updated?.length - 1);
      }
      return updated;
    });
  };

  const dismissAll = () => {
    setAlerts([]);
    setCurrentIndex(0);
  };

  const handleAction = (alert) => {
    if (onAlertAction) onAlertAction(alert);
  };

  if (alerts?.length === 0) return null;

  const currentAlert = alerts?.[currentIndex] || alerts?.[0];
  const alertStyle = ALERT_TYPES?.[currentAlert?.type] || ALERT_TYPES?.info;

  return (
    <div
      className={`w-full ${alertStyle?.bg} ${alertStyle?.text} border-b ${alertStyle?.borderColor}`}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="flex items-center gap-3 px-4 md:px-6 py-2.5">
        {/* Alert Icon */}
        <div className="flex-shrink-0">
          <Icon name={alertStyle?.icon} size={16} color="currentColor" strokeWidth={2.5} />
        </div>

        {/* Alert Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-caption font-medium text-sm">{currentAlert?.title}</span>
            <span className="font-caption text-xs opacity-80 hidden sm:inline">{currentAlert?.message}</span>
            <span className="font-caption text-xs opacity-70 flex-shrink-0">{currentAlert?.timestamp}</span>
          </div>
          {/* Mobile: show message on second line */}
          <p className="font-caption text-xs opacity-80 sm:hidden mt-0.5 truncate">{currentAlert?.message}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {currentAlert?.actionLabel && (
            <button
              className={`font-caption text-xs font-medium underline underline-offset-2 transition-opacity duration-200 hover:opacity-80 focus:outline-none focus-visible:ring-1 focus-visible:ring-current rounded px-1 min-h-[28px]`}
              onClick={() => handleAction(currentAlert)}
            >
              {currentAlert?.actionLabel}
            </button>
          )}

          {/* Alert counter */}
          {alerts?.length > 1 && (
            <div className="flex items-center gap-1">
              <button
                className="flex items-center justify-center w-6 h-6 rounded opacity-80 hover:opacity-100 transition-opacity focus:outline-none disabled:opacity-40"
                onClick={() => setCurrentIndex((p) => Math.max(0, p - 1))}
                disabled={currentIndex === 0}
                aria-label="Previous alert"
              >
                <Icon name="ChevronLeft" size={14} color="currentColor" strokeWidth={2.5} />
              </button>
              <span className="font-caption text-xs opacity-80 min-w-[32px] text-center">
                {currentIndex + 1}/{alerts?.length}
              </span>
              <button
                className="flex items-center justify-center w-6 h-6 rounded opacity-80 hover:opacity-100 transition-opacity focus:outline-none disabled:opacity-40"
                onClick={() => setCurrentIndex((p) => Math.min(alerts?.length - 1, p + 1))}
                disabled={currentIndex === alerts?.length - 1}
                aria-label="Next alert"
              >
                <Icon name="ChevronRight" size={14} color="currentColor" strokeWidth={2.5} />
              </button>
            </div>
          )}

          {/* Dismiss current */}
          {currentAlert?.dismissible && (
            <button
              className="flex items-center justify-center w-7 h-7 rounded opacity-80 hover:opacity-100 transition-opacity focus:outline-none focus-visible:ring-1 focus-visible:ring-current min-h-[28px] min-w-[28px]"
              onClick={() => dismissAlert(currentAlert?.id)}
              aria-label="Dismiss this alert"
            >
              <Icon name="X" size={15} color="currentColor" strokeWidth={2.5} />
            </button>
          )}

          {/* Dismiss all (only when multiple) */}
          {alerts?.length > 1 && (
            <button
              className="hidden sm:flex items-center gap-1 font-caption text-xs opacity-70 hover:opacity-100 transition-opacity focus:outline-none focus-visible:ring-1 focus-visible:ring-current rounded px-1 min-h-[28px]"
              onClick={dismissAll}
              aria-label="Dismiss all alerts"
            >
              <span>Clear all</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}